import { h, Component, Host, State, Element, Prop, Watch, Listen, Method, Event, EventEmitter } from "@stencil/core";
import Fragment from "../../common/fragment";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import pfx from "../../utils/style-class-prefix-helper";
import { scrollToElement } from "../../utils/scroll-helper";
import { LINK_SCHEME_MAP } from "../link/constants/link-constants";
import { BUTTON_SCHEME_MAP } from "../button/constants/button-constants";
import { getCssVariable, setCssVariable } from "../../utils/css-variables";
import {
  CtaTarget,
  CtaType,
  ThirdLevelNavigationSchemeDefinition,
  ThirdLevelNavigationSchemeName
} from "./constants/third-level-navigation-types";
import {
  CTA_TARGET_SELF,
  CTA_TYPE_BUTTON,
  CTA_TYPE_NONE,
  THIRD_LEVEL_NAVIGATION_SCHEME_CONFIG,
  THIRD_LEVEL_NAVIGATION_SCHEME_MAP
} from "./constants/third-level-navigation-constants";
import { BackgroundColor } from "../background/constants/background-types";
import { KeyIds } from "../../constants/keys";
import { ThirdLevelNavigationSchemeConfiguration } from "../../constants/colors";
import { hexToRgb } from "../../utils/filter-helper";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - Holds any number of eon-ui-third-level-navigation-items. The one with an
 *  attribute of active="true" will be marked as the selected item (underlying border).
 */
@Component({
  tag: "eon-ui-third-level-navigation",
  styleUrl: "third-level-navigation.scss",
  shadow: true
})
export class ThirdLevelNavigation
  implements ColorableComponent<ThirdLevelNavigationSchemeConfiguration<ThirdLevelNavigationSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  highlightBorder: HTMLElement;
  activeNavItem: HTMLElement;
  scrollWrapper: HTMLElement;
  textButton: HTMLEonUiButtonElement;
  iconButton: HTMLEonUiButtonElement;
  textLink: HTMLEonUiLinkElement;
  iconLink: HTMLEonUiLinkElement;
  navigationItems: Array<HTMLEonUiThirdLevelNavigationItemElement>;

  schemeConfig: ThirdLevelNavigationSchemeConfiguration<ThirdLevelNavigationSchemeDefinition> = THIRD_LEVEL_NAVIGATION_SCHEME_CONFIG;
  schemeMap = THIRD_LEVEL_NAVIGATION_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: ThirdLevelNavigationSchemeDefinition;

  /**
   * Indicate if the horizontal gradient should be shown to indicate that horizontal scroll is available.
   */
  @State() leftGradientVisible: boolean = false;
  @State() rightGradientVisible: boolean = false;

  @State() highlightedItemIndex: number = 0;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiThirdLevelNavigationElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ThirdLevelNavigationSchemeName;

  /**
   * Prop to control the presence of CTA and select its type (button/link)
   */
  @Prop() ctaType: CtaType = CTA_TYPE_NONE;

  /**
   * Prop to control the presence of CTA and select its type (button/link)
   */
  @Prop() ctaTarget: CtaTarget = CTA_TARGET_SELF;

  /**
   * Label for the CTA, only applied if ctaType isn't "none"
   */
  @Prop() ctaLabel: string;

  /**
   * Href for the CTA, only applied if ctaType isn't "none"
   */
  @Prop() ctaHref: string;

  /**
   * Enables the presence of the CTA at the right for small screen for space optimization.
   */
  @Prop() showCtaOnMobile: boolean = false;

  /**
   * Icon for the CTA button when showCtaOnMobile is "true".
   */
  @Prop() ctaIcon: string;

  /**
   * Raised when the component has been loaded.
   */
  @Event() thirdLevelNavigationLoad: EventEmitter;

  /**
   * Method to evaluate the component height
   */
  @Method()
  async updateHeight() {
    return await this.hostElement.getBoundingClientRect().height;
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  componentWillLoad() {
    this.applyScheme();
    this.registerHTMLElements();
    this.passSchemeToChildren();
  }

  componentDidLoad() {
    this.setCssVariables();

    this.setGradientColorVariable();

    // In componentWillLoad the navigation items haven't width yet so we can't update the gradient.
    this.updateGradientVisible();

    // It's a valid case that there is no active navigation item.
    if (this.activeNavItem) {
      scrollToElement(this.activeNavItem, {
        scrollContainer: this.scrollWrapper,
        targetPosition: "center",
        direction: "horizontally",
        firstChild: this.navigationItems[0]
      });
      this.placeHighlightBorder();
    }

    this.thirdLevelNavigationLoad.emit();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("activeScheme")
  passSchemeToChildren() {
    // This method is responsible to keep slotted components in sync with third-level-navigation scheme
    // when it changes during the life of the component. This mostly applies to storybook controls in
    // canvas tab where changes to props don't trigger component reloading (In docs tab it does).

    if (Array.isArray(this.navigationItems)) {
      for (let navItem of this.navigationItems) {
        navItem.setAttribute("scheme", this.scheme);
      }
    }

    if (this.textButton || this.textLink) {
      const backgroundColor = `eon-${this.scheme}`;

      if (this.textButton) {
        this.textButton.setAttribute("scheme", BUTTON_SCHEME_MAP[backgroundColor as BackgroundColor]);
      }
      if (this.iconButton) {
        this.iconButton.setAttribute("scheme", BUTTON_SCHEME_MAP[backgroundColor as BackgroundColor]);
      }
      if (this.textLink) {
        this.textLink.setAttribute("scheme", LINK_SCHEME_MAP[backgroundColor as BackgroundColor]);
      }
      if (this.iconLink) {
        this.iconLink.setAttribute("scheme", LINK_SCHEME_MAP[backgroundColor as BackgroundColor]);
      }
    }
  }

  @Watch("highlightedItemIndex")
  updateHighlightedItem() {
    if (!Array.isArray(this.navigationItems)) {
      return;
    }

    this.navigationItems[this.highlightedItemIndex].focusInnerLink();

    const highlightedItem = this.navigationItems[this.highlightedItemIndex];
    scrollToElement(highlightedItem, {
      scrollContainer: this.scrollWrapper,
      targetPosition: "center",
      direction: "horizontally",
      firstChild: this.navigationItems[0]
    });
  }

  @Listen("resize", { target: "window" })
  onResizeWindow() {
    this.updateGradientVisible();

    // It's a valid case that there is no active navigation item.
    if (this.activeNavItem) {
      scrollToElement(this.activeNavItem, {
        scrollContainer: this.scrollWrapper,
        targetPosition: "center",
        direction: "horizontally",
        firstChild: this.navigationItems[0]
      });
      this.placeHighlightBorder();
    }
  }

  private setGradientColorVariable() {
    let overflowGradientColor = hexToRgb(
      getCssVariable(this.hostElement, "--contentBackgroundColor").replace(/ /g, "")
    );

    setCssVariable(this.hostElement, "--overflowGradientColor", `${overflowGradientColor}`);
  }

  private placeHighlightBorder() {
    // It's a valid case that there is no active navigation item.
    if (this.activeNavItem) {
      // We calculate left value from the first navigationItem.
      const activeItemLeft =
        this.activeNavItem.getBoundingClientRect().left - this.navigationItems[0].getBoundingClientRect().left;
      this.highlightBorder.setAttribute(
        "style",
        `width: ${this.activeNavItem.offsetWidth}px; left: ${activeItemLeft}px;`
      );
    }
  }

  private updateGradientVisible() {
    if (!Array.isArray(this.navigationItems) || !this.navigationItems.length) {
      return;
    }

    const firstItem = this.navigationItems[0];
    const lastItem = this.navigationItems[this.navigationItems.length - 1];

    const startOfFirstItem = firstItem.getBoundingClientRect().left;
    const startOfContainer = this.scrollWrapper.getBoundingClientRect().left;

    const endOfLastItem = lastItem.getBoundingClientRect().width + lastItem.getBoundingClientRect().left;
    const endOfContainer =
      this.scrollWrapper.getBoundingClientRect().width + this.scrollWrapper.getBoundingClientRect().left;

    // Since this items are scrolled their left position can return decimal values which break the condition.
    const roundStartOfFirstItem = Math.floor(startOfFirstItem);
    const roundStartOfContainer = Math.floor(startOfContainer);
    const roundEndOfLastItem = Math.floor(endOfLastItem);

    this.leftGradientVisible = roundStartOfFirstItem < roundStartOfContainer;
    this.rightGradientVisible = roundEndOfLastItem > endOfContainer;
  }

  private handleScrollItems() {
    this.updateGradientVisible();
  }

  private handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case KeyIds.LEFT:
        const previousTabIndex =
          (this.highlightedItemIndex + this.navigationItems.length - 1) % this.navigationItems.length;
        this.highlightedItemIndex = previousTabIndex;
        event.preventDefault();
        break;
      case KeyIds.RIGHT:
        // allow the first item to be highlighted when initially pressing RIGHT
        const nextTabIndex =
          this.highlightedItemIndex === -1 ? 0 : (this.highlightedItemIndex + 1) % this.navigationItems.length;
        this.highlightedItemIndex = nextTabIndex;
        event.preventDefault();
        break;
      case KeyIds.TAB:
        // We handle navigation using tab key as well. As long as the next index is inside the component we prevent
        // the default. If the user reaches the last or first item we let her/him keep navigating the page.
        if (event.shiftKey) {
          const newIndex = this.highlightedItemIndex - 1;

          if (newIndex > -1) {
            this.highlightedItemIndex = newIndex;
            event.preventDefault();
          }
        } else {
          const newIndex = this.highlightedItemIndex + 1;

          if (newIndex < this.navigationItems.length) {
            this.highlightedItemIndex = newIndex;
            event.preventDefault();
          }
        }
        break;
      case KeyIds.SPACE:
        this.navigationItems[this.highlightedItemIndex].clickInnerLink();
        event.preventDefault();
        break;
      default:
        break;
    }
  }

  private registerHTMLElements() {
    // We transform the HTMLCollection into a regular array to use emethods like .find().
    const children = this.hostElement.querySelectorAll("eon-ui-third-level-navigation-item");
    this.navigationItems = Array.prototype.slice.call(children, 0) as Array<HTMLEonUiThirdLevelNavigationItemElement>;
    const firstChild = this.navigationItems[0];
    firstChild.setAttribute("first-nav-item", "true");

    // Extract the active one.
    this.activeNavItem = this.navigationItems.find((child) => child.getAttribute("active") === "true");
  }

  render() {
    return (
      <Host>
        <eon-ui-background color={this.activeScheme.contentBackgroundColor}>
          <div class={pfx("nav-items-wrapper")}>
            <div class={pfx("nav-items")}>
              {/* Focus will happen inside this element in a slot so we are ok with disabling this rule */}
              {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
              <div
                class={pfx({
                  "scroll-window": true,
                  "gradient-left": this.leftGradientVisible,
                  "gradient-right": this.rightGradientVisible
                })}
                onKeyDown={this.handleKeyDown.bind(this)}
                role="grid"
              >
                <div
                  class={pfx("scroll-wrapper")}
                  onScroll={this.handleScrollItems.bind(this)}
                  ref={(ref) => {
                    this.scrollWrapper = ref;
                  }}
                  role="row"
                >
                  <slot />
                  <div
                    class={pfx("highlight-border")}
                    ref={(ref) => {
                      this.highlightBorder = ref;
                    }}
                  ></div>
                </div>
              </div>
              {this.ctaType !== CTA_TYPE_NONE && (
                <div class={pfx("cta-wrapper")}>
                  {this.ctaType === CTA_TYPE_BUTTON ? (
                    <Fragment>
                      <eon-ui-button
                        class={pfx("text-button")}
                        text={this.ctaLabel}
                        href={this.ctaHref}
                        target={this.ctaTarget}
                        ref={(ref) => {
                          this.textButton = ref;
                        }}
                        size="small"
                        useAsLink={true}
                      ></eon-ui-button>
                      {this.showCtaOnMobile && (
                        <eon-ui-button
                          class={pfx("icon-button")}
                          href={this.ctaHref}
                          icon={this.ctaIcon}
                          target={this.ctaTarget}
                          ref={(ref) => {
                            this.iconButton = ref;
                          }}
                          size="small"
                          useAsLink={true}
                        ></eon-ui-button>
                      )}
                    </Fragment>
                  ) : (
                    <Fragment>
                      <eon-ui-link
                        class={pfx({ "text-link": true })}
                        text={this.ctaLabel}
                        href={this.ctaHref}
                        icon={this.ctaIcon}
                        target={this.ctaTarget}
                        ref={(ref) => {
                          this.textLink = ref;
                        }}
                      ></eon-ui-link>
                      {this.showCtaOnMobile && (
                        <eon-ui-link
                          class={pfx("icon-link")}
                          href={this.ctaHref}
                          icon={this.ctaIcon}
                          target={this.ctaTarget}
                          ref={(ref) => {
                            this.iconLink = ref;
                          }}
                        ></eon-ui-link>
                      )}
                    </Fragment>
                  )}
                </div>
              )}
            </div>
          </div>
        </eon-ui-background>
      </Host>
    );
  }
}
